import React from 'react';
import { useDesignMode } from '../../contexts/DesignContext';
import { classNames } from '../../utils/classNames';

interface SketchProgressBarProps {
  value: number;
  max?: number;
  className?: string;
  /** Bar = dashed track; scribble = hand-drawn SVG line (track detail). */
  variant?: 'bar' | 'scribble';
  'data-testid'?: string;
}

export const SketchProgressBar: React.FC<SketchProgressBarProps> = ({
  value,
  max = 100,
  className,
  variant = 'bar',
  'data-testid': testId,
}) => {
  const { isSketchy } = useDesignMode();
  const pct = Math.min(100, Math.max(0, (value / max) * 100));

  if (isSketchy && variant === 'scribble') {
    const endX = 2 + pct * 2.96;
    return (
      <svg
        data-testid={testId}
        className={classNames('w-full h-3 sketch-progress-scribble', className)}
        viewBox="0 0 300 12"
        preserveAspectRatio="none"
        aria-hidden={testId ? undefined : true}
        role={testId ? 'progressbar' : undefined}
        aria-valuenow={testId ? Math.round(pct) : undefined}
        aria-valuemin={testId ? 0 : undefined}
        aria-valuemax={testId ? 100 : undefined}
      >
        <path
          d="M2 8 Q40 4 80 7 T160 5 T240 8 T298 6"
          fill="none"
          stroke="var(--color-border)"
          strokeWidth="2"
          opacity="0.4"
        />
        <path
          d={`M2 8 Q40 4 80 7 T160 5 T${endX} 6`}
          fill="none"
          stroke="var(--brand-from)"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (!isSketchy) {
    const isComplete = pct >= 100;
    return (
      <div
        data-testid={testId}
        className={classNames('h-2 bg-[var(--color-hover)] rounded-full overflow-hidden', className)}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={classNames(
            'h-full transition-all duration-300',
            isComplete ? 'bg-gradient-to-r from-emerald-500 to-green-500' : 'bg-[var(--brand-from)]'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={classNames('sketch-progress-track', className)}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="sketch-progress-fill transition-all duration-300" style={{ width: `${pct}%` }} />
    </div>
  );
};
